import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import {
	Form,
	Input,
	Button,
	Space,
	Skeleton,
	Row,
	Col,
	Checkbox,
	Radio,
	Select,
} from 'antd'
import { connect, useDispatch, useSelector } from 'react-redux'

import { postData } from '../../../../Actions/Action'
import types from '../../../../Stores/types'
import FedexAccountRegistrationForm from '../Small/FedexAccountRegistrationForm'
import { FedexConnectionSettingsBanner } from '../FedexMigrationBanner'

const BTN_COLOR = '#479ccf'
const primaryBtnStyle = { backgroundColor: BTN_COLOR, borderColor: BTN_COLOR, color: '#fff' }

// ── LTL-specific connected view shown as step 5 of the registration wizard ──
function LtlStep5({ step1Values, credentials, handleReconnect, onValidate, initialSettings, onFinalSave, resolvedApiType, handleApiTypeChange }) {
	const [ltlForm] = Form.useForm()
	const [accountType, setAccountType] = useState('shipper')
	const [testType, setTestType] = useState(false)
	const hasInitialized = useRef(false)

	const isJustRegistered = !!(step1Values?.account_number)

	useEffect(() => {
		if (hasInitialized.current) return
		hasInitialized.current = true
		ltlForm.setFieldsValue({
			shipping_account_number: step1Values?.account_number || initialSettings?.shipping_account_number || initialSettings?.account_number || '',
			billing_address: step1Values?.address || initialSettings?.billing_address || '',
			billing_city: step1Values?.city || initialSettings?.billing_city || '',
			billing_state: step1Values?.state || initialSettings?.billing_state || '',
			billing_zip: step1Values?.postal_code || initialSettings?.billing_zip || '',
			billing_country: step1Values?.country || initialSettings?.billing_country || 'US',
			physical_address: initialSettings?.physical_address || '',
			physical_city: initialSettings?.physical_city || '',
			physical_state: initialSettings?.physical_state || '',
			physical_zip: initialSettings?.physical_zip || '',
			physical_country: initialSettings?.physical_country || '',
			third_party_account: initialSettings?.third_party_account || '',
			nickname: step1Values?.nickname || initialSettings?.nickname || '',
		})
	}, [step1Values, initialSettings, ltlForm])

	const copyBilling = useCallback(
		copy => {
			if (copy) {
				const vals = ltlForm.getFieldsValue(['billing_address', 'billing_city', 'billing_state', 'billing_zip', 'billing_country'])
				ltlForm.setFieldsValue({
					physical_address: vals.billing_address,
					physical_city: vals.billing_city,
					physical_state: vals.billing_state,
					physical_zip: vals.billing_zip,
					physical_country: vals.billing_country,
				})
			} else {
				ltlForm.setFieldsValue({ physical_address: '', physical_city: '', physical_state: '', physical_zip: '', physical_country: '' })
			}
		},
		[ltlForm]
	)

	const onFinish = values => {
		const payload = {
			child_key: credentials.child_key,
			child_secret: credentials.child_secret,
			clientId: credentials.child_key,
			clientSecret: credentials.child_secret,
			account_number: step1Values?.account_number || initialSettings?.account_number || '',
			shipping_account_number: values.shipping_account_number,
			billing_address: values.billing_address,
			billing_city: values.billing_city,
			billing_state: values.billing_state,
			billing_zip: values.billing_zip,
			billing_country: values.billing_country,
			physical_address: values.physical_address,
			physical_city: values.physical_city,
			physical_state: values.physical_state,
			physical_zip: values.physical_zip,
			physical_country: values.physical_country,
			third_party_account: values.third_party_account,
			nickname: values.nickname,
			account_type: accountType,
		}
		if (testType) {
			onValidate({ ...payload, registration_step: 'test' })
		} else {
			onFinalSave(payload)
		}
	}

	return (
		<div>
			{isJustRegistered && (
				<div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, padding: '16px 20px', marginBottom: 16 }}>
					<div style={{ color: '#389e0d', fontWeight: 700, fontSize: '15px' }}>FedEx Account Connected Successfully</div>
					<div style={{ color: '#52c41a', fontSize: '13px', marginTop: 4 }}>Your FedEx account credentials have been verified and saved.</div>
				</div>
			)}

			{resolvedApiType !== undefined && (
				<Form layout='vertical' size='large' style={{ marginBottom: 0 }}>
					<Form.Item label='Which API Will You Connect To?' style={{ marginBottom: 12 }}>
						<Select
							value={resolvedApiType}
							options={[
								{ label: 'Legacy API', value: 'legacy_api' },
								{ label: 'REST API (pre June 1, 2026)', value: 'new_api' },
								{ label: 'REST MFA API (eff. June 1, 2026)', value: 'account_registration_api' },
							]}
							onChange={handleApiTypeChange}
						/>
					</Form.Item>
				</Form>
			)}

			<Form layout='vertical' size='large' form={ltlForm} onFinish={onFinish}>
				<Form.Item className='mb-1' label='Nickname' name='nickname' required
					rules={[{ required: !testType, message: 'Nickname is required' }]}>
					<Input placeholder='e.g., FedEx LTL' />
				</Form.Item>

				<Form.Item label='Child Key'>
					<Input type='password' value={credentials.child_key} disabled />
				</Form.Item>

				<Form.Item label='Child Secret'>
					<Input type='password' value={credentials.child_secret} disabled />
				</Form.Item>

				<Form.Item label='Shipper Account Number' name='shipping_account_number'>
					<Input disabled />
				</Form.Item>

				<Row gutter={30}>
					<Col span={12}>
						<Form.Item label='Billing Address' name='billing_address' required
							rules={[{ required: testType, message: 'Billing Address is required' }]}>
							<Input type='text' placeholder='Billing Address' disabled />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='billing_city' style={{ marginTop: 48 }}
							rules={[{ required: testType, message: 'City is required' }]}>
							<Input type='text' placeholder='City' disabled />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={30}>
					<Col span={12}>
						<Form.Item name='billing_state' required rules={[{ required: testType, message: 'State is required' }]}>
							<Input type='text' placeholder='State e.g. CA' maxLength={2} disabled />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='billing_zip' required rules={[{ required: testType, message: 'Zip Code is required' }]}>
							<Input type='text' placeholder='Zip Code' disabled />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={30}>
					<Col span={12}>
						<Form.Item name='billing_country' required rules={[{ required: testType, message: 'Country is required' }]}>
							<Input type='text' placeholder='Country e.g. US' maxLength={2} disabled />
						</Form.Item>
					</Col>
				</Row>

				<Form.Item name='copy_billing'>
					<Checkbox onChange={e => copyBilling(e.target.checked)}>
						Copy billing address to physical address.
					</Checkbox>
				</Form.Item>

				<Row gutter={30}>
					<Col span={12}>
						<Form.Item label='Physical Address' name='physical_address' required
							rules={[{ required: testType, message: 'Shipping Address is required' }]}>
							<Input type='text' placeholder='Shipping Address' />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='physical_city' style={{ marginTop: 48 }}
							rules={[{ required: testType, message: 'City is required' }]}>
							<Input type='text' placeholder='City' />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={30}>
					<Col span={12}>
						<Form.Item name='physical_state' required rules={[{ required: testType, message: 'State is required' }]}>
							<Input type='text' placeholder='State e.g. CA' maxLength={2} />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item name='physical_zip' required rules={[{ required: testType, message: 'Zip Code is required' }]}>
							<Input type='text' placeholder='Zip Code' />
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={30}>
					<Col span={12}>
						<Form.Item name='physical_country' required rules={[{ required: testType, message: 'Country is required' }]}>
							<Input type='text' placeholder='Country e.g. US' maxLength={2} />
						</Form.Item>
					</Col>
				</Row>

				<Form.Item className='mb-1' label='Third Party Account Number' name='third_party_account'
					rules={[{ required: accountType === 'thirdParty', message: 'Third Party Account Number is required' }]}>
					<Input type='text' />
				</Form.Item>

				<Form.Item className='mb-0' name='account_type' rules={[{ required: false }]}>
					<Radio.Group defaultValue={accountType}>
						<Radio value='shipper' onClick={() => setAccountType('shipper')}>
							Test Shipper Account Number
						</Radio>
						<Radio value='thirdParty' onClick={() => setAccountType('thirdParty')}>
							Test Third Party Account Number
						</Radio>
					</Radio.Group>
				</Form.Item>

				<Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
					<Space>
						<Button type='primary' size='large' style={primaryBtnStyle} htmlType='submit'
							onClick={() => setTestType(true)}>
							Test Connection
						</Button>
						<Button type='primary' size='large' style={primaryBtnStyle} htmlType='submit'
							onClick={() => setTestType(false)}>
							Save Settings
						</Button>
						<Button type='primary' size='large' style={primaryBtnStyle} onClick={handleReconnect}>
							Reconnect Account
						</Button>
					</Space>
				</Form.Item>
			</Form>
		</div>
	)
}

// ── Main connection settings component ──────────────────────────────────────
function ConnectionSettingsComponent(props) {
	const [connectionState, setConnectionState] = useState({
		testType: false,
		skeleton_loading: true,
	})
	const [form] = Form.useForm()
	const [accountType, setAccountType] = useState('shipper')
	const [capturedNickname, setCapturedNickname] = useState('')
	const apiTypeInitialized = useRef(false)
	const dispatch = useDispatch()
	const { connectionSettings, token, FedexLTLApiType, isInstalling } = useSelector(state => state)
	const url = 'https://eniture.com/bigcommerce-fedex-freight-api-connection-instructions/'
	const freightDirectUrl = 'https://www.fedexfreight.com/en-us/services/freight-direct'
	const freightType = Form.useWatch('freight_type', form) ?? props.connectionSettings?.freight_type

	const isAccountRegApi = FedexLTLApiType === 'account_registration_api'

	useEffect(() => {
		if (connectionSettings && !apiTypeInitialized.current) {
			apiTypeInitialized.current = true
			if (!(connectionSettings?.api_type) && (connectionSettings?.carrierId)) {
				connectionSettings['api_type'] = 'legacy_api'
			}
			// account_registration_api is disabled for LTL — fall back to new_api
			const resolvedType = connectionSettings?.api_type === 'account_registration_api'
				? 'new_api'
				: (connectionSettings?.api_type ?? 'new_api')
			dispatch({
				type: types.SET_FEDEX_LTL_API_TYPE,
				payload: resolvedType,
			})
		}
	}, [connectionSettings, dispatch])

	const handleTypeChange = type => {
		setConnectionState({ ...connectionState, testType: type })
	}

	useEffect(() => {
		form.validateFields()
	}, [accountType, form])

	const copyBillingAdressValues = useCallback(
		copy => {
			const fields = ['billing_address', 'billing_city', 'billing_state', 'billing_zip', 'billing_country']
			if (copy) {
				const copiedValues = form.getFieldsValue(fields)
				form.setFieldsValue({
					physical_address: copiedValues[fields[0]],
					physical_city: copiedValues[fields[1]],
					physical_state: copiedValues[fields[2]],
					physical_zip: copiedValues[fields[3]],
					physical_country: copiedValues[fields[4]],
				})
			} else {
				form.setFieldsValue({
					physical_address: '', physical_city: '', physical_state: '',
					physical_zip: '', physical_country: '',
				})
			}
		},
		[form]
	)

	const onFinish = values => {
		values = { ...props.connectionSettings, ...values, api_type: FedexLTLApiType }
		values.testType = connectionState.testType
		values.installed_carrier_id = props.carrierId
		values.carrierId = props.carrierId
		values.account_type = accountType
		if (connectionState.testType) delete values.is_installing
		props.postData(values, props.token)
	}

	if (props.connectionSettings === null || props.connectionSettings === undefined) {
		return <Skeleton active />
	} else {
		if (Object.keys(props.connectionSettings)?.length === 0) {
			props.connectionSettings.access_level = 'pro'
		}
	}

	return (
		<Fragment>
			{/* <FedexConnectionSettingsBanner
				apiType={FedexLTLApiType === 'legacy_api' ? 'legacy_api' : null}
				customMessage='FedEx has deprecated the API you are using. You must reconnect to FedEx using its new REST API. Edit the FedEx Connection Settings, select the Rest API, and complete the steps to re-establish the connection.'
			/> */}
			{!isAccountRegApi && (
				<div className={'note-bx'}>
					<strong>Note!</strong>{' '}
					You must have a FedEx Freight account to use this application. If you don't have one, contact FedEx Freight at 866-393-4585 or{' '}
					<a href='https://www.fedexfreight.com/en-us/rate-ship/open-account' target='_blank' rel='noreferrer'>register online</a>.
				</div>
			)}
			{
				<div className={'note-bx'}>
					<>
						FedEx Freight service marks are owned by FedEx Freight Holding Company, Inc. and are used by permission.
					</>
				</div>
			}

			{/* Legacy / New API form */}
			{!isAccountRegApi && (
				<Form layout='vertical' name='connection_settings' className='connection-settings'
					size={'large'} form={form} initialValues={props.connectionSettings} onFinish={onFinish}>

					

					<Form.Item className='mb-1' label='Which API Will You Connect To?'>
						<Select
							value={FedexLTLApiType}
							options={[
								{ label: 'SOAP', value: 'legacy_api' },
								{ label: 'REST', value: 'new_api' },
								// { label: 'REST MFA API (eff. June 1, 2026)', value: 'account_registration_api' },
							]}
							onChange={opt => {
							if (opt === 'account_registration_api') {
								const typed = form.getFieldValue('nickname')
								if (typed) setCapturedNickname(typed)
							}
							dispatch({ type: types.SET_FEDEX_LTL_API_TYPE, payload: opt })
						}}
						/>
					</Form.Item>

					<Form.Item className='mb-1' label='Nickname' name='nickname' required
						rules={[{ required: !connectionState.testType, message: 'Nickname is required' }]}>
						<Input placeholder='e.g. FedEx Freight East Coast' />
					</Form.Item>

					<Form.Item className='mb-1' label='Account Type' name='freight_type' initialValue='ltl_freight'>
						<Select
							options={[
								{ label: 'LTL Freight', value: 'ltl_freight' },
								{ label: 'Freight Direct', value: 'freight_direct' },
							]}
						/>
					</Form.Item>

					{FedexLTLApiType === 'new_api' ? (
						<>
							<Form.Item label='FedEx Freight Account Number' name='shipping_account_number' required
								rules={[{ required: true, message: 'FedEx Freight Account Number is required' }]}>
								<Input type='text' placeholder='e.g. 209033858' maxLength={50} />
							</Form.Item>

							<Form.Item label='Third-party FedEx Freight Account Number' name='third_party_account'
								rules={[{ required: accountType === 'thirdParty', message: 'Third-party Account Number is required' }]}>
								<Input type='text' placeholder='e.g. 987654321' />
							</Form.Item>

							<Form.Item label='Account Name' name='account_name'>
								<Input type='text' placeholder='e.g. ABC Logistics LLC' maxLength={100} />
							</Form.Item>

							<Row gutter={30}>
								<Col span={12}>
									<Form.Item label='Address' name='physical_address' required
										rules={[{ required: true, message: 'Address is required' }]}>
										<Input type='text' placeholder='e.g. 123 Main St, Suite 200' />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label='City' name='physical_city' required
										rules={[{ required: true, message: 'City is required' }]}>
										<Input type='text' placeholder='e.g. Memphis' />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={30}>
								<Col span={12}>
									<Form.Item label='State/Province' name='physical_state' required
										rules={[{ required: true, message: 'State/Province is required' }]}>
										<Input type='text' placeholder='e.g. TN' maxLength={2} />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item label='ZIP/Postal Code' name='physical_zip' required
										rules={[{ required: true, message: 'ZIP/Postal Code is required' }]}>
										<Input type='text' placeholder='e.g. 38116' />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={30}>
								<Col span={12}>
									<Form.Item label='Country' name='physical_country' required
										rules={[{ required: true, message: 'Country is required' }]}>
										<Input type='text' placeholder='e.g. US' maxLength={2} />
									</Form.Item>
								</Col>
							</Row>
						</>
					) : (
						<>
							<Form.Item label='Billing Account Number' name='account_number' required
								rules={[{ required: connectionState.testType, message: 'Billing Account Number is required' }]}>
								<Input placeholder='Billing Account Number' maxLength={50} />
							</Form.Item>
							<Form.Item label='Meter Number' name='meter_number' required
								rules={[{ required: connectionState.testType, message: 'Meter Number is required' }]}>
								<Input placeholder='Meter Number' maxLength={50} />
							</Form.Item>
							<Form.Item label='Password' name='password' required
								rules={[{ required: connectionState.testType, message: 'Password is required' }]}>
								<Input type='text' placeholder='Password' maxLength={100} />
							</Form.Item>
							<Form.Item label='Authentication Key' name='api_access_key' required
								rules={[{ required: connectionState.testType, message: 'Authentication Key is required' }]}>
								<Input placeholder='Authentication Key' />
							</Form.Item>

							<Form.Item label='Fedex Freight Account Number' name='shipping_account_number' required
								rules={[{ required: connectionState.testType && accountType === 'shipper', message: 'Shipper Account Number is required' }]}>
								<Input type='text' placeholder='e.g. 209033858' maxLength={50} />
							</Form.Item>
							
							<Form.Item className='mb-1' label='Third-party Fedex Freight Account Number' name='third_party_account'
								rules={[{ required: accountType === 'thirdParty', message: 'Third Party Account Number is required' }]}>
								<Input type='text' placeholder='e.g. 987654321' />
							</Form.Item>

							<Row gutter={30}>
								<Col span={12}>
									<Form.Item label='Billing Address' name='billing_address' required
										rules={[{ required: connectionState.testType, message: 'Billing Address is required' }]}>
										<Input type='text' placeholder='Billing Address e.g. 123 Main St, Suite 200' />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name='billing_city' style={{ marginTop: 48 }}
										rules={[{ required: connectionState.testType, message: 'City is required' }]}>
										<Input type='text' placeholder='City e.g. Memphis' />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={30}>
								<Col span={12}>
									<Form.Item name='billing_state' required rules={[{ required: connectionState.testType, message: 'State is required' }]}>
										<Input type='text' placeholder='State e.g. TN' maxLength={2} />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name='billing_zip' required rules={[{ required: connectionState.testType, message: 'Zip Code is required' }]}>
										<Input type='text' placeholder='Zip Code e.g. 38116' />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={30}>
								<Col span={12}>
									<Form.Item name='billing_country' required rules={[{ required: connectionState.testType, message: 'Country is required' }]}>
										<Input type='text' placeholder='Country e.g. US' maxLength={2} />
									</Form.Item>
								</Col>
							</Row>

							<Form.Item name='remember'>
								<Checkbox onChange={e => copyBillingAdressValues(e.target.checked)}>
									Copy billing address to physical address.
								</Checkbox>
							</Form.Item>

							<Row gutter={30}>
								<Col span={12}>
									<Form.Item label='Physical Address' name='physical_address' required
										rules={[{ required: connectionState.testType, message: 'Shipping Address is required' }]}>
										<Input type='text' placeholder='Shipping Address e.g. 123 Main St, Suite 200' />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name='physical_city' style={{ marginTop: 48 }}
										rules={[{ required: connectionState.testType, message: 'City is required' }]}>
										<Input type='text' placeholder='City e.g. Memphis' />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={30}>
								<Col span={12}>
									<Form.Item name='physical_state' required rules={[{ required: connectionState.testType, message: 'State is required' }]}>
										<Input type='text' placeholder='State e.g. TN' maxLength={2} />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name='physical_zip' required rules={[{ required: connectionState.testType, message: 'Zip Code is required' }]}>
										<Input type='text' placeholder='Zip Code e.g. 38116' />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={30}>
								<Col span={12}>
									<Form.Item name='physical_country' required rules={[{ required: connectionState.testType, message: 'Country is required' }]}>
										<Input type='text' placeholder='Country e.g. US' maxLength={2} />
									</Form.Item>
								</Col>
							</Row>


							{freightType !== 'freight_direct' && (
								<div>
									<a href={url} target='_blank' rel='noreferrer'>
										How to obtain your FedEx Freight API authentication credentials?
									</a>
								</div>
							)}
						</>
					)}

					{freightType === 'freight_direct' && (
						<div>
							<a href={freightDirectUrl} target='_blank' rel='noreferrer'>
								What is Freight Direct?
							</a>
						</div>
					)}

					<Form.Item className='mb-0' name='account_type' rules={[{ required: false }]}>
						<Radio.Group defaultValue={accountType}>
							<Radio value='shipper' onClick={() => { setAccountType('shipper'); form.validateFields() }}>
								{FedexLTLApiType === 'new_api' ? 'Test FedEx Freight Account Number' : 'Test Shipper Account Number'}
							</Radio>
							<Radio value='thirdParty' onClick={() => { setAccountType('thirdParty'); form.validateFields(['thirdParty']) }}>
								{FedexLTLApiType === 'new_api' ? 'Test Third-party FedEx Freight Account Number' : 'Test Third Party Account Number'}	
							</Radio>
						</Radio.Group>
					</Form.Item>

					<Form.Item style={{ textAlign: 'right', marginBottom: '0' }}>
						<Space>
							<Button type='primary' size={'large'} htmlType='submit' name='test' onClick={() => handleTypeChange(true)}>
								Test Connection
							</Button>
							<Button type='primary' size={'large'} htmlType='submit' name='save' loading={false} onClick={() => handleTypeChange(false)}>
								Save Settings
							</Button>
						</Space>
					</Form.Item>
				</Form>
			)}

			{/* Account Registration API wizard — commented out, MFA not active for LTL */}
			{/* {isAccountRegApi && (
				<FedexAccountRegistrationForm
					loading={isInstalling}
					apiResponse={connectionSettings}
					initialSettings={{ ...connectionSettings, nickname: capturedNickname || connectionSettings?.nickname }}
					successData={connectionSettings?.registration_success ? connectionSettings : null}
					nicknamePlaceholder='e.g., FedEx LTL'
					apiTypeValue={FedexLTLApiType}
					onApiTypeChange={value => dispatch({ type: types.SET_FEDEX_LTL_API_TYPE, payload: value })}
					onBack={() => dispatch({ type: types.SET_FEDEX_LTL_API_TYPE, payload: 'new_api' })}
					onValidate={values => {
						const payload = { ...values, testType: true, api_type: 'account_registration_api', carrierId: props.carrierId }
						props.postData(payload, props.token)
					}}
					onSave={values => {
						const payload = {
							...props.connectionSettings,
							api_type: 'account_registration_api',
							child_key: values.child_key,
							child_secret: values.child_secret,
							clientId: values.child_key,
							clientSecret: values.child_secret,
							account_number: values.account_number ?? '',
							shipping_account_number: values.account_number ?? '',
							billing_address: values.address ?? '',
							billing_city: values.city ?? '',
							billing_state: values.state ?? '',
							billing_zip: values.postal_code ?? '',
							billing_country: values.country ?? 'US',
							nickname: values.nickname ?? '',
							carrierId: props.carrierId,
							installed_carrier_id: props.carrierId,
							testType: false,
						}
						props.postData(payload, props.token)
					}}
					step5Content={({ step1Values, credentials, handleReconnect, onValidate, initialSettings, resolvedApiType, handleApiTypeChange }) => (
						<LtlStep5
							step1Values={step1Values}
							credentials={credentials}
							handleReconnect={handleReconnect}
							onValidate={onValidate}
							initialSettings={initialSettings}
							resolvedApiType={resolvedApiType}
							handleApiTypeChange={handleApiTypeChange}
							onFinalSave={ltlValues => {
								props.postData({
									...props.connectionSettings,
									...ltlValues,
									api_type: 'account_registration_api',
									child_key: credentials.child_key,
									child_secret: credentials.child_secret,
									clientId: credentials.child_key,
									clientSecret: credentials.child_secret,
									carrierId: props.carrierId,
									installed_carrier_id: props.carrierId,
									testType: false,
								}, props.token)
							}}
						/>
					)}
				/>
			)} */}
		</Fragment>
	)
}

const mapStateToProps = state => {
	return {
		connectionSettings: state.connectionSettings,
		skeleton_loading: state.skeleton_loading,
		token: state.token,
		carrierId: state.carrierId,
	}
}

const mapDispatchToProps = dispatch => {
	return {
		postData: (data, token) =>
			dispatch(
				postData(
					data,
					'GET_CONNECTION_SETTINGS',
					'submit_connection_settings',
					token
				)
			),
	}
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ConnectionSettingsComponent)
